import { useCallback, useEffect } from 'react';
import { track } from '@churnsignal/churnsignal-sdk-web';

import { useAuth } from '@/modules/Auth/hooks/useAuth';

/**
 * Hook for tracking user events with automatic user email inclusion
 */
export const useTracking = () => {
  const { user } = useAuth();

  const getUserEmail = useCallback((): string | undefined => {
    if (!user) return undefined;
    return user.attributes?.email || user.signInUserSession?.idToken?.payload?.email;
  }, [user]);

  const trackEvent = useCallback(
    (eventName: string, properties?: Record<string, any>) => {
      const userEmail = getUserEmail();
      // SDK requires an email (or prior identify()); do not call track() without it.
      if (!userEmail) return;
      track(eventName, properties || {}, userEmail);
    },
    [getUserEmail],
  );

  const trackPageView = useCallback(
    (pageName: string, additionalProperties?: Record<string, any>) => {
      trackEvent('page_viewed', {
        page: pageName,
        ...additionalProperties,
      });
    },
    [trackEvent],
  );

  return {
    trackEvent,
    trackPageView,
    getUserEmail,
  };
};

/**
 * Hook to automatically track page views when component mounts
 */
export const usePageTracking = (pageName: string, additionalProperties?: Record<string, any>) => {
  const { trackPageView } = useTracking();

  useEffect(() => {
    trackPageView(pageName, additionalProperties);
  }, [pageName]); // eslint-disable-line react-hooks/exhaustive-deps
};
